import { DateTime } from 'luxon';
import { type ReactNode } from 'react';
import styled from 'styled-components';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem.tsx';
import { ExpressionType } from '../../../../expressions/IExpression.ts';
import { expressionToString } from '../../../../expressions/preview/expressionToString.tsx';
import { type IAssignableExpression } from '../../../../expressions/repository/assignable/IAssignableExpression.ts';
import {
  ComputedDateOffsetUnit,
  type IComputedDateExpression,
} from '../../../../expressions/repository/computed-date/IComputedDateExpression.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps.ts';
import { type IComputedDateWidget } from '../IComputedWidget.ts';

type IComputedDateWidgetPreviewProps =
  IWidgetComponentProps<IComputedDateWidget>;

const ExpressionDisplay = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  padding: 9px 11px 7px;
  font-size: 14px;
  line-height: 1.5;
  background-color: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  transition: all 0.2s;
`;

const OffsetPart = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ReferenceChip = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colorPrimary};
  color: white;
  font-size: 14px;
  font-weight: bold;
  border-radius: 4px;
  padding: 4px 8px;
  line-height: 1.25;
`;

const unitLabels: Record<ComputedDateOffsetUnit, string> = {
  [ComputedDateOffsetUnit.Days]: 'giorni',
  [ComputedDateOffsetUnit.Months]: 'mesi',
  [ComputedDateOffsetUnit.Years]: 'anni',
};

/**
 * Renderizza un'assignable expression in preview, mostrando un chip per i
 * riferimenti e formattando le date literal quando richiesto.
 */
function renderAssignable(
  expr: IAssignableExpression,
  formatAsDate = false,
): ReactNode {
  if (expr.type === ExpressionType.Reference) {
    return <ReferenceChip>@{expr.value.label}</ReferenceChip>;
  }

  if (expr.type === ExpressionType.Value) {
    if (expr.value == null) return null;

    if (formatAsDate) {
      return DateTime.fromFormat(String(expr.value), 'yyyy-MM-dd').toFormat(
        'dd/MM/yyyy',
      );
    }

    return String(expr.value);
  }
  return expressionToString(expr);
}

/**
 * Restituisce una chiave stabile per un'assignable expression, utile per
 * costruire key React prevedibili durante il rendering degli offset.
 */
function getAssignableKey(expr: IAssignableExpression): string {
  if (expr.type === ExpressionType.Reference) {
    return expr.value.id;
  }

  if (expr.type === ExpressionType.Value) {
    return String(expr.value);
  }

  return expressionToString(expr);
}

type IComputedDateOffset = IComputedDateExpression['offsets'][number];

/**
 * Restituisce true solo quando l'amount è un valore literal numerico >= 0.
 * In questo caso in preview viene mostrato il prefisso "+".
 */
function isPositiveOffsetAmount(offset: IComputedDateOffset): boolean {
  if (offset.amount.type !== ExpressionType.Value) {
    return false;
  }

  if (offset.amount.value == null) {
    return false;
  }

  return Number(offset.amount.value) >= 0;
}

/**
 * Compone la key React dell'offset combinando unità, tipo e valore.
 * L'indice viene mantenuto come ultimo discriminante per evitare collisioni.
 */
function buildOffsetKey(offset: IComputedDateOffset, index: number): string {
  return `${offset.unit}-${offset.amount.type}-${getAssignableKey(offset.amount)}-${index}`;
}

export function ComputedDateWidgetPreview(
  props: IComputedDateWidgetPreviewProps,
) {
  const options = getWidgetProps(props);
  const { expression } = props.widget;

  return (
    <ConfigFormItem
      name="date"
      label={<RichTextAdvancedRender inline text={options.label} />}
      help={
        options.helpText ? (
          <RichTextAdvancedRender inline text={options.helpText} />
        ) : undefined
      }
      layout={{ hideVisualLabel: options.hideLabel }}
      readonly={options.readonly}
      widget={props.widget}
    >
      <ExpressionDisplay>
        {expression?.from?.type && (
          <>
            {renderAssignable(expression.from, true)}
            {expression.offsets?.map((offset, i) => {
              const isPositiveValue = isPositiveOffsetAmount(offset);
              const offsetKey = buildOffsetKey(offset, i);

              return (
                <OffsetPart key={offsetKey}>
                  {isPositiveValue && '+'}
                  {renderAssignable(offset.amount)}
                  {' ' + unitLabels[offset.unit]}
                </OffsetPart>
              );
            })}
          </>
        )}
      </ExpressionDisplay>
    </ConfigFormItem>
  );
}
